<template>
  <v-container fluid class="pa-0 ma-0">
    <v-row v-if="!hideCostCalculation && initialized">
      <v-col v-if="!hideRate" sm="6" :md="!allowToDefineRateSheet ? 4 : 6">
        <MoneyTextField
          :initialValue="baseCost.rate"
          :label="isBaseCostForm ? $t('baseRate') : $t('rate')"
          :focus="focus"
          :expectedValue="expectedValue"
          @val-changed="rateEntered"
          :extraRules="['validActivityRate']"
        />
      </v-col>

      <v-col sm="6" v-if="allowToDefineRateSheet">
        <v-text-field
        :value="baseCost?.rateSheet?.name"
        readonly
        :label="$t('rateSheet')"
        class="mb-n6"
        :hint="hint"
        :persistent-hint="!!hint"
        @mousedown="defineRateSheetClicked()">
          <template #append>
            <Icon
            icon="mdi-pencil"
            :tooltipText="$t('defineRateSheet')"
            margin=""
            :small="false"
            @icon-clicked="defineRateSheetClicked()"/>
          </template>
        </v-text-field>
      </v-col>

      <v-col sm="6" :md="!allowToDefineRateSheet ? 4 : 6">
        <PayVariationSelector
          type="PayOn"
          class="pay-on"
          :disabled="isPayOnDisabled"
          :initialValue="baseCost.payOn"
          :accountingCategory="accountingCategory"
          :isLogsContract="isLogsContract"
          :payByValue="baseCost.payBy"
          @pay-on-chosen="payOnChosen"
        />
      </v-col>

      <v-col v-if="!hidePayBy" sm="6" :md="!allowToDefineRateSheet ? 4 : 6">
        <PayVariationSelector
          type="PayBy"
          class="pay-by"
          :initialValue="baseCost.payBy"
          :accountingCategory="accountingCategory"
          :propItems="payByTypes"
          :disabled="payByTypes?.length === 1"
          @pay-by-chosen="payByChosen"
        />
      </v-col>
    </v-row>

    <span v-else-if="initialized && !hideRate">
      <MoneyTextField
        :initialValue="baseCost.rate"
        :label="isBaseCostForm ? $t('baseRate') : $t('rate')"
        :expectedValue="expectedValue"
        @val-changed="rateEntered"
        :extraRules="['validActivityRate']"
      />
    </span>

    <Dialog :stateId="dialogId" @close="closeDialog" @click:outside="closeDialog" @dialog-closing="closeDialog">
      <RateSheetEditor
      v-if="displayRateSheetDialog"
      :accountId="accountId"
      :initDefinition="propBaseCost?.rateSheet?.rateSheetDefinition"
      :existingRateSheet="baseCost.rateSheet"
      :activity="activity"
      :contract="contract"
      @confirm="rateSheetConfirmed"
      @cancel="closeDialog"/>
    </Dialog>
  </v-container>
</template>

<script>
import { PayBy, PayOn } from '@/utils/Enumerations.js'
import cloneDeep from 'lodash/cloneDeep.js'
import { mapActions, mapGetters } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers.js'
import BaseCost from '@/model/BaseCost.js'

export default {
  name: 'BaseCostForm',

  components: {
    PayVariationSelector: () => import('../autocomplete/PayVariationSelect.vue'),
    MoneyTextField: () => import('../helper/MoneyTextField.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    RateSheetEditor: () => import('@/components/activity/rate-sheet/RateSheetEditor.vue')
  },

  props: {
    propBaseCost: { type: BaseCost, default: undefined },
    accountingCategory: { Number, default: 0 },
    editing: { Boolean, default: false },
    focus: { Boolean, default: false },
    hidePayBy: { Boolean, default: false },
    hideCostCalculation: { Boolean, default: false },
    isBaseCostForm: { Boolean, default: false },
    expectedValue: { Number, default: undefined, required: false },
    isLogsContract: { type: Boolean, default: true },
    contract: { type: Object, required: false },
    activity: { type: Object, required: false },
    accountId: { type: Number, default: undefined },
    allowRateSheet: { type: Boolean, default: false },
    payByTypes: { type: Array, default: undefined },
    hideRate: { type: Boolean, default: false },
    rateSheetHint: { type: Boolean, default: false }
  },

  data: () => ({
    baseCost: new BaseCost({
      rate: 0,
      payBy: 1,
      payOn: 0,
      rateSheet: null
    }),
    savedPayOn: 0,
    savedRate: 0,
    initialized: false,
    dialogId: uniqueDialogId('base-cost-form'),
    displayRateSheetDialog: false,
    accountRateSheetDefinitionsFetched: false
  }),

  watch: {
    baseCost: {
      handler (newVal) {
        this.$emit('base-cost-changed', newVal)
      },
      deep: true
    },

    'baseCost.payBy': {
      async handler (newVal) {
        if (newVal !== PayBy.Volume.value) {
          this.baseCost.rateSheetId = null
        } else {
          const defectPayOns = [PayOn.Defect.value, PayOn.NatureDefect.value, PayOn.MismanufacturedDefect.value]
          if (defectPayOns.includes(this.baseCost.payOn)) {
            this.baseCost.payOn = PayOn.Net.value
          }
          if (!this.accountRateSheetDefinitionsFetched && this.accountId) {
            this.accountRateSheetDefinitionsFetched = true
            await this.fetchAccountRateSheetDefinitions(this.accountId)
          }
        }
      }
    },

    payByTypes: {
      handler () {
        if (this.payByTypes == null || this.payByTypes.length < 1) return
        if (!this.payByTypes.some(pb => pb.value === this.baseCost.payBy)) {
          this.baseCost.payBy = this.payByTypes[0].value
        }
      },
      deep: true,
      immediate: true
    }
  },

  computed: {
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions']),
    ...mapGetters('account', ['allAccounts']),
    isPayOnDisabled () {
      return this.baseCost.payBy === PayBy.Load.value
    },

    allowToDefineRateSheet () {
      return this.baseCost.payBy === PayBy.Volume.value && this.allowRateSheet
    },

    hint () {
      if (this.rateSheetHint !== true) return undefined
      if (this.accountRateSheetDefinitions.length > 0) return undefined
      const account = this.allAccounts.find(a => a.accountId === this.accountId)
      if (account == null) return undefined
      return this.$t('noRateSheetDefsDefined', { account: account.name })
    }
  },

  async created () {
    if (this.propBaseCost) {
      this.baseCost = cloneDeep(this.propBaseCost)
    }
    if (this.baseCost.rateSheetId != null && this.baseCost.rateSheetId > 0) {
      this.baseCost.rateSheet ??= await this.fetchRateSheet(this.baseCost.rateSheetId)
    }
    this.initialized = true
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('rate-sheet-definition', ['fetchAccountRateSheetDefinitions', 'fetchVersionedRateSheetDefinition']),

    rateEntered (rate) { this.baseCost.rate = rate },

    payOnChosen (payOn) { this.baseCost.payOn = payOn },

    payByChosen (payBy) {
      if (payBy === PayBy.Load.value) {
        this.savedPayOn = this.baseCost.payOn
        this.savedRate = this.baseCost.rate
        this.baseCost.payOn = PayOn.NotApplicable.value
        this.$emit('is-by-load', true)
      } else if (this.baseCost.payBy === PayBy.Load.value) {
        this.baseCost.payOn = this.savedPayOn
        this.baseCost.rate = this.savedRate
        this.$emit('is-by-load', false)
      }
      this.baseCost.payBy = payBy
    },

    openDialog (width = '80vw') {
      this.openOrUpdateDialog({ id: this.dialogId, width })
    },

    closeDialog () {
      this.displayRateSheetDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    defineRateSheetClicked () {
      this.displayRateSheetDialog = true
      this.openDialog('90vw')
    },

    rateSheetConfirmed (rateSheet) {
      this.baseCost.rateSheet = rateSheet
      this.closeDialogsAtOrAbove(this.dialogId)
    }
  }
}
</script>
